import React, { useState } from 'react';
import { useRecognitionMutations, SubmitRecognitionRequest, useRecognitionTypes } from '../../hooks/useRecognition';
import { useBrgList } from '../../hooks/useBrgList';
import { useEventsByBrg } from '../../hooks/useEvents';
import { DateInputDDMMYYYY } from '../../components/shared/DateInputDDMMYYYY';

interface SubmitRecognitionModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface FormErrors {
  [key: string]: string;
}

export default function SubmitRecognitionModal({ onClose, onSuccess }: SubmitRecognitionModalProps) {
  const [formData, setFormData] = useState({
    recipientName: '',
    brgId: '',
    eventId: '',
    recognitionTypeId: '',
    anticipatedAwardDate: '',
    reason: '',
    description: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  const { data: brgs } = useBrgList();
  const { data: types } = useRecognitionTypes();
  const { data: events = [] } = useEventsByBrg(formData.brgId ? parseInt(formData.brgId) : null);
  const { submitRecognition } = useRecognitionMutations();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Reset eventId when BRG changes
    if (name === 'brgId') {
      setFormData(prev => ({ ...prev, [name]: value, eventId: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.recipientName.trim()) newErrors.recipientName = 'Recipient name is required';
    if (formData.recipientName.length > 100) newErrors.recipientName = 'Max 100 characters';

    if (!formData.brgId) newErrors.brgId = 'BRG is required';
    if (!formData.recognitionTypeId) newErrors.recognitionTypeId = 'Recognition type is required';

    if (!formData.anticipatedAwardDate) newErrors.anticipatedAwardDate = 'Date is required';

    if (!formData.reason.trim()) newErrors.reason = 'Reason is required';
    if (formData.reason.length > 500) newErrors.reason = 'Max 500 characters';

    if (formData.description.length > 2000) newErrors.description = 'Max 2000 characters';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormComplete = () => {
    return (
      formData.recipientName.trim() !== '' &&
      formData.brgId !== '' &&
      formData.recognitionTypeId !== '' &&
      formData.anticipatedAwardDate !== '' &&
      formData.reason.trim() !== ''
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const request: SubmitRecognitionRequest = {
        RecipientName: formData.recipientName.trim(),
        BrgId: parseInt(formData.brgId),
        EventId: formData.eventId ? parseInt(formData.eventId) : 0,
        RecognitionTypeId: parseInt(formData.recognitionTypeId),
        AnticipatedAwardDate: formData.anticipatedAwardDate,
        Reason: formData.reason.trim(),
        Description: formData.description.trim() || undefined,
      };

      await submitRecognition.mutateAsync(request);
      onSuccess();
    } catch (error: any) {
      console.error('Submit error:', error);
      if (error.response?.data?.fieldErrors) {
        setErrors(error.response.data.fieldErrors);
      } else {
        setErrors({ submit: error.message || 'Failed to submit recognition' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1a3bcc',
            color: 'white',
            padding: '14px 18px',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 800 }}>Submit Moment Maker Recognition</h2>
            <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>Recognize an individual or guest</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: 'pointer',
              fontWeight: 700,
              padding: 0,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
          {/* Notice Banner */}
          <div style={{ backgroundColor: '#f0f4ff', borderLeft: '4px solid #1a3bcc', padding: '10px 14px', fontSize: '11px', color: '#333', marginBottom: '14px', borderRadius: '0 4px 4px 0' }}>
            <strong style={{ color: '#1a3bcc' }}>Reminder:</strong> Each recognition must be linked to a BRG event, or to the Admin-Only area for BRG leader recognitions.
          </div>

          {/* Recipient Name */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Recipient Name <span style={{ color: '#a01a23' }}>*</span>
            </label>
            <input
              type="text"
              name="recipientName"
              value={formData.recipientName}
              onChange={handleChange}
              placeholder="Who is being recognized?"
              style={{
                width: '100%',
                padding: '7px 10px',
                border: errors.recipientName ? '2px solid #a01a23' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            {errors.recipientName && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.recipientName}</p>}
          </div>

          {/* BRG & Event - Row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
            {/* BRG */}
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                BRG <span style={{ color: '#a01a23' }}>*</span>
              </label>
              <select
                name="brgId"
                value={formData.brgId}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  border: errors.brgId ? '2px solid #a01a23' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '12px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%23666' d='M0 0l5 6 5-6z'/%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  paddingRight: '28px',
                }}
              >
                <option value="">Select BRG</option>
                {brgs?.map(b => (
                  <option key={b.brgId} value={b.brgId}>{b.name}</option>
                ))}
              </select>
              {errors.brgId && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.brgId}</p>}
            </div>

            {/* Event */}
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
                Linked BRG Event
              </label>
              <select
                name="eventId"
                value={formData.eventId}
                onChange={handleChange}
                disabled={!formData.brgId}
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  border: errors.eventId ? '2px solid #a01a23' : '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '12px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  appearance: 'none',
                  backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%23666' d='M0 0l5 6 5-6z'/%3e%3c/svg%3e")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  paddingRight: '28px',
                  opacity: !formData.brgId ? 0.5 : 1,
                }}
              >
                <option value="">Select Event</option>
                {events.map(e => (
                  <option key={e.eventId} value={e.eventId}>{e.eventName}</option>
                ))}
              </select>
              {errors.eventId && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.eventId}</p>}
            </div>
          </div>

          {/* Type */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Recognition Type <span style={{ color: '#a01a23' }}>*</span>
            </label>
            <select
              name="recognitionTypeId"
              value={formData.recognitionTypeId}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '7px 10px',
                border: errors.recognitionTypeId ? '2px solid #a01a23' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%23666' d='M0 0l5 6 5-6z'/%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 8px center',
                paddingRight: '28px',
              }}
            >
              <option value="">Select Recognition Type</option>
              {types?.map(t => (
                <option key={t.recognitionTypeId} value={t.recognitionTypeId}>
                  {t.label} — {t.points} pts / ${t.dollarValue}
                </option>
              ))}
            </select>
            {errors.recognitionTypeId && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.recognitionTypeId}</p>}
          </div>

          {/* Award Date */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Anticipated Award Date <span style={{ color: '#a01a23' }}>*</span>
            </label>
            <DateInputDDMMYYYY
              value={formData.anticipatedAwardDate}
              onChange={(val) => handleChange({ target: { name: 'anticipatedAwardDate', value: val } } as React.ChangeEvent<HTMLInputElement>)}
              style={{
                width: '100%',
                padding: '7px 10px',
                border: errors.anticipatedAwardDate ? '2px solid #a01a23' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            {errors.anticipatedAwardDate && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.anticipatedAwardDate}</p>}
          </div>

          {/* Reason */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Reason for Recognition <span style={{ color: '#a01a23' }}>*</span>
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              placeholder="Briefly describe what this person did to earn this recognition..."
              rows={3}
              style={{
                width: '100%',
                padding: '7px 10px',
                border: errors.reason ? '2px solid #a01a23' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
            {errors.reason && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.reason}</p>}
            <p style={{ fontSize: '11px', color: '#999', marginTop: '3px', margin: '3px 0 0 0', textAlign: 'right' }}>
              {formData.reason.length}/500
            </p>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Recognition Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Provide a detailed description of how this recognition supports the BRG and its members..."
              rows={3}
              style={{
                width: '100%',
                padding: '7px 10px',
                border: errors.description ? '2px solid #a01a23' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
            {errors.description && <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>{errors.description}</p>}
            <p style={{ fontSize: '11px', color: '#999', marginTop: '3px', margin: '3px 0 0 0', textAlign: 'right' }}>
              {formData.description.length}/2000
            </p>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                backgroundColor: '#fff',
                color: '#444',
                padding: '8px 16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '12px',
                width: '100px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !isFormComplete()}
              style={{
                backgroundColor: '#1a3bcc',
                color: 'white',
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading || !isFormComplete() ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: '12px',
                opacity: isLoading || !isFormComplete() ? 0.6 : 1,
                width: '100px',
              }}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
